import { AuthToken, OpconQueryPayload } from '../types';
import { createHeaders, verifyApiResponse } from './api';
import ApiError from './apiError';
import { decodeOpconRequest, OpConApiType } from './decoder/opConDecoder';
import { MAX_API_RESPONSE_WAIT_TIME } from './helpers/constants';

const OPCON_POLL_INTERVAL = 1000;
const OPCON_SOURCE = 'web-performance';

/**
 * Fetches OpCon report data using the async API:
 *  1. POST /async/reports  → 201 Created + Location header
 *  2. Poll GET {location}  → 404 while processing, 200 when ready
 */
export async function fetchOpcon(baseUrl: string, payload: OpconQueryPayload, authToken: AuthToken) {
    const headers = createHeaders(authToken);

    // POST to trigger async report generation
    const postResponse = await fetch(`${baseUrl}/async/reports`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ ...payload, source: OPCON_SOURCE }),
    });

    if (postResponse.status >= 400) {
        await verifyApiResponse(postResponse);
    }

    const location = postResponse.headers.get('Location');
    if (!location) {
        throw new ApiError(postResponse.status, 'POST /async/reports did not return a Location header');
    }

    // Location header is absolute (e.g. https://api.opcon.rio.cloud/v1/reports/{id})
    const reportUrl = location.startsWith('http') ? location : `${baseUrl}${location}`;

    // Poll GET until the report is available
    const rawResponse = await pollForReport(reportUrl, headers, MAX_API_RESPONSE_WAIT_TIME, OPCON_POLL_INTERVAL);
    const entities = decodeOpconRequest(rawResponse);

    return {
        entities,
        attributes: rawResponse.attributes || [],
    };
}

/**
 * Polls GET /reports/{id} until the report is ready.
 * The backend returns 404 while the report is still being generated
 * and 200 with the full JSON payload once complete.
 */
export async function pollForReport(
    url: string,
    headers: Record<string, string>,
    timeout: number,
    interval: number
): Promise<OpConApiType> {
    const endTime = Date.now() + timeout;

    return new Promise((resolve, reject) => {
        const check = async () => {
            try {
                const response = await fetch(url, { method: 'GET', headers });

                if (response.status === 200) {
                    resolve(await response.json());
                } else if (response.status === 404) {
                    // Report still being generated — keep polling
                    if (Date.now() < endTime) {
                        setTimeout(check, interval);
                    } else {
                        reject(new Error('OpCon report generation timed out'));
                    }
                } else {
                    // Unexpected status — reject with details
                    try {
                        await verifyApiResponse(response);
                    } catch (err) {
                        reject(err);
                    }
                }
            } catch (error) {
                reject(error);
            }
        };
        check();
    });
}
